import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { Valence } from '@upright/common/model/types';
import { BouColor } from '@upright/ui/common/generic/global/bou-colors';
import { targetLensLayout } from '@upright/ui/common/upright/core/impact-lenses/targets/common/layout';
import { useTargetsLensContext } from '@upright/ui/common/upright/core/impact-lenses/targets/common/use-targets-lens-context';
import styled, { css } from 'styled-components';
import { valenceToSide } from '../../../impact-lens-profile/common/utils';
import TargetIcon from '@upright/ui/icons/img/manual/target.svg';
import { useCarouselLayout } from '../../../impact-lens-profile/context';
import { isTargetHighLighted, onTargetDataClick } from './on-target-click';
import { PlaceholderSVG } from '../../../impact-lens-profile/common/components/placeholder';
import { mergeTargets } from '../common/utils';
import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { usePushGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';

interface Props {
  impact: ImpactCategoryId;
  valence: Valence;
}

export function TargetsSymbol(props: Props) {
  const { impact, valence } = props;
  const baseLayout = useCarouselLayout();
  const ctx = useTargetsLensContext();
  const pushGtmEvent = usePushGtmEvent();
  const entityTargets = ctx?.data.targets;
  const sbtiTargets = ctx?.data.sbtiTargets;
  const allTargets = mergeTargets(entityTargets, sbtiTargets);

  if (!allTargets || (!entityTargets && !sbtiTargets)) {
    return <></>;
  }
  const layout = targetLensLayout(baseLayout, allTargets);
  const targets = allTargets.filter(d => d.impactCategoryId === impact && d.valence === valence);
  const bounds = layout.impactTargetSymbolArea(valenceToSide(valence), impact);
  const hasTargets = targets.length > 0;

  const borderColor = hasTargets ? BouColor.TOOL_TEXT_DARK_GRAY : BouColor.TOOL_BORDER_LIGHTER_GRAY;
  const transform = `translate(${bounds.x1},${bounds.y1})`;

  const isHighlighted = isTargetHighLighted(ctx.selection, impact, valence);

  return (
    <G
      clickable={hasTargets}
      borderColor={borderColor}
      onClick={() => {
        if (hasTargets) {
          pushGtmEvent({
            event: CustomGtmEvent.IMPACT_PROFILE_BAR_CLICK,
            data: {
              impactCode: impact,
              valence,
              lens: ImpactLensType.TARGETS,
            },
          });
          onTargetDataClick(ctx.selection, ctx.setSelection, impact, valence);
        }
      }}
      isHighlighted={isHighlighted}
    >
      <Clickable bounds={baseLayout.impactDataArea(valenceToSide(props.valence), props.impact)} />
      {hasTargets && (
        <g transform={transform}>
          <TargetIcon fill={borderColor} width={bounds.width} height={bounds.height} />
        </g>
      )}
      {!hasTargets && (
        <Circle
          strokeWidth={1}
          cx={bounds.x1 + bounds.width / 2}
          cy={bounds.y1 + bounds.height / 2}
          r={bounds.height / 2}
        />
      )}
    </G>
  );
}

const Circle = styled.circle`
  fill: transparent;
  stroke: currentColor;
`;

const Clickable = styled(PlaceholderSVG)`
  opacity: 0 !important;
`;

const G = styled.g<{ isHighlighted: boolean; borderColor: string; clickable: boolean }>`
  pointer-events: all;
  ${p =>
    css`
      color: ${p.borderColor}; // Base color selection for both circle and icon
    `};
  ${p =>
    p.isHighlighted
      ? css`
          // Selection state goes here
          color: black;
        `
      : ''};
  ${p =>
    p.clickable
      ? css`
          cursor: pointer;
          &:hover {
            // Hovering state goes here
            color: black;
          }
        `
      : ''};
`;
